// GA4 Analytics module for Google Analytics 4 implementation
import appConfig from 'shared-module/config/appConfig'
import switches from 'shared-module/config/switches'

// Load gtag.js script dynamically
const loadGtagScript = (measurementId) => {
  return new Promise((resolve, reject) => {
    if (window.gtag) {
      resolve()
      return
    }

    // Create script element for gtag.js
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
    script.onload = () => {
      // Initialize dataLayer and gtag function
      window.dataLayer = window.dataLayer || []
      window.gtag = function () {
        window.dataLayer.push(arguments)
      }
      resolve()
    }
    script.onerror = reject

    document.head.appendChild(script)
  })
}

const initializeGA4 = async () => {
  if (appConfig.ga4?.switch !== switches.on || !appConfig.ga4?.measurementId) {
    return
  }

  try {
    await loadGtagScript(appConfig.ga4.measurementId)

    // Set current timestamp
    window.gtag('js', new Date())

    // Configure GA4 with measurement ID and optional config
    const config = {
      ...appConfig.ga4.config,
      // Add debug mode for development
      debug_mode: process.env.NODE_ENV === 'development',
    }

    window.gtag('config', appConfig.ga4.measurementId, config)

    console.log('GA4 initialized successfully with ID:', appConfig.ga4.measurementId)
  } catch (error) {
    console.error('Failed to initialize GA4:', error)
  }
}

const sendPageView = (pageTitle, pageLocation) => {
  if (window.gtag && appConfig.ga4?.switch === switches.on) {
    window.gtag('event', 'page_view', {
      page_title: pageTitle,
      page_location: pageLocation,
    })
  }
}

// Send custom event
const sendEvent = (action, parameters = {}) => {
  if (window.gtag && appConfig.ga4?.switch === switches.on) {
    window.gtag('event', action, parameters)
  }
}

// Send ecommerce purchase event (GA4 format)
const sendPurchaseEvent = (transactionData) => {
  if (window.gtag && appConfig.ga4?.switch === switches.on) {
    window.gtag('event', 'purchase', {
      transaction_id: transactionData.transaction_id,
      value: transactionData.value,
      currency: transactionData.currency,
      items: transactionData.items?.filter(item => item != null).map(item => ({
        item_id: item.item_id,
        item_name: item.item_name,
        category: item.category || 'General',
        quantity: item.quantity,
        price: item.price,
        item_variant: item.item_variant,
      })),
    })
  }
}

// Set user ID
const setUserId = (userId) => {
  if (window.gtag && appConfig.ga4?.switch === switches.on) {
    window.gtag('config', appConfig.ga4.measurementId, {
      user_id: userId,
    })
  }
}

// Set user properties
const setUserProperties = (properties) => {
  if (window.gtag && appConfig.ga4?.switch === switches.on) {
    window.gtag('config', appConfig.ga4.measurementId, {
      user_properties: properties,
    })
  }
}

const GA4Analytics = {
  initialize: initializeGA4,
  sendPageView,
  sendEvent,
  sendPurchaseEvent,
  setUserId,
  setUserProperties,
}

export default GA4Analytics
